import { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Boxes, PackagePlus, ShoppingCart, ArrowLeftRight, Receipt, Wallet, Printer, Users, Warehouse, HandCoins, CalendarRange, Truck, ShoppingBag, FileText, Container, ShieldCheck,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { HeaderBar } from "./HeaderBar";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/products", key: "products", label: "Products & Stock", icon: Boxes },
  { to: "/sell", key: "sell", label: "New Sale · Receipt", icon: ShoppingBag },
  { to: "/quotations", key: "quotations", label: "Quotations", icon: FileText },
  { to: "/stock-in", key: "stock-in", label: "Stock In", icon: PackagePlus },
  { to: "/stock-out", key: "stock-out", label: "Stock Out · Sales", icon: ShoppingCart },
  { to: "/transfers", key: "transfers", label: "Transfers", icon: ArrowLeftRight },
  { to: "/expenses", key: "expenses", label: "Expenses", icon: Receipt },
  { to: "/debtors", key: "debtors", label: "Customer Credit", icon: HandCoins },
  { to: "/payables", key: "payables", label: "Supplier Credit", icon: Truck },
  { to: "/cashflow", key: "cashflow", label: "Cashflow", icon: Wallet },
  { to: "/report", key: "daily-report", label: "Daily Report", icon: Printer },
  { to: "/period-report", key: "period-report", label: "Weekly / Monthly", icon: CalendarRange },
];
const ADMIN_NAV = [
  { to: "/trucks", key: "trucks", label: "Trucks", icon: Container },
  { to: "/users", key: "users", label: "Users", icon: Users },
  { to: "/depots", key: "depots", label: "Depots", icon: Warehouse },
];
const OWNER_NAV = { to: "/sign-ins", key: "sign-ins", label: "Sign-in history", icon: ShieldCheck };

function NavItem({ item, prefix, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`${prefix}-${item.key}-button`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
          isActive ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500" : "text-slate-400 hover:text-slate-100 hover:bg-slate-900"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function SidebarNav({ prefix = "nav", onNavigate }) {
  const { isAdmin, user } = useAuth();
  const truckOnly = user?.role === "truck_manager";
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 pl-5 pr-10 h-16 border-b border-slate-800">
        <div className="h-8 w-8 rounded bg-amber-500 grid place-items-center text-slate-950 font-display font-extrabold text-lg">M</div>
        <div className="leading-none">
          <div className="font-display text-base font-bold leading-tight tracking-wide uppercase" data-testid="sidebar-brand-name">MATWIGA ENTERPRISES LEDGER</div>
          <div className="label-mono !text-[9px] mt-0.5">Hardware inventory</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        <div className="label-mono px-3 pt-2 pb-2">{truckOnly ? "Truck fleet" : "Operations"}</div>
        {truckOnly
          ? <NavItem item={ADMIN_NAV[0]} prefix={prefix} onNavigate={onNavigate} />
          : NAV.map((item) => <NavItem key={item.key} item={item} prefix={prefix} onNavigate={onNavigate} />)}
        {isAdmin && (
          <>
            <div className="label-mono px-3 pt-5 pb-2">Owner</div>
            {[...ADMIN_NAV, ...(user.is_owner ? [OWNER_NAV] : [])].map((item) => <NavItem key={item.key} item={item} prefix={prefix} onNavigate={onNavigate} />)}
          </>
        )}
      </nav>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  if (user?.role === "truck_manager" && !location.pathname.startsWith("/trucks")) return <Navigate to="/trucks" replace />;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="no-print hidden lg:flex fixed inset-y-0 left-0 z-30 w-64 flex-col border-r border-slate-800 bg-slate-950">
        <SidebarNav />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-slate-950 border-slate-800">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarNav prefix="mobile-nav" onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="lg:pl-64 print:pl-0 flex min-h-screen flex-col">
        <HeaderBar onMenu={() => setOpen(true)} />
        <main className="flex-1 p-4 md:p-6 lg:p-8 print:p-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
